import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MessageCircle,
  Search,
  Users,
  Send,
  AlertTriangle,
  CheckCheck,
  Filter,
  Plus,
  X,
  ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { chatAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getSocket } from '../../services/socket';

/* ── role code → Arabic display name (matches StaffManagement) ── */
const ROLE_DISPLAY = {
  owner: 'مدير النظام',
  lab_supervisor: 'مشرف المختبر',
  lab_specialist: 'أخصائي مختبر',
  lab_accountant: 'محاسب',
  contractor_lab: 'مختبر متعاقد',
  super_admin: 'مدير عام',
  cashier: 'كاشير',
  receptionist: 'استقبال',
};
const roleLabel = (roleObj) => {
  const code = roleObj?.name || roleObj?.name_ar || '';
  return ROLE_DISPLAY[code] || roleObj?.name_ar || code || 'موظف';
};

const formatTime = (dateValue) => {
  if (!dateValue) return '';
  return new Date(dateValue).toLocaleString('ar-SA-u-ca-gregory', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  });
};

const LabChat = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [messages, setMessages] = useState([]);

  const [showNewThread, setShowNewThread] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  const [newThreadMessage, setNewThreadMessage] = useState('');
  const [newThreadImportant, setNewThreadImportant] = useState(false);

  const [replyMessage, setReplyMessage] = useState('');
  const [replyImportant, setReplyImportant] = useState(false);
  const [sending, setSending] = useState(false);

  /* mobile: show threads list vs messages */
  const [mobileView, setMobileView] = useState('threads'); // 'threads' | 'messages'

  const messagesEndRef = useRef(null);

  const selectedThread = threads.find((t) => String(t._id) === String(selectedThreadId));
  const currentUserId = user?._id;

  /* unique role codes from employees list (for filter dropdown) */
  const roles = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      const roleName = e?.role_id?.name;
      if (roleName) set.add(roleName);
    });
    return Array.from(set).sort();
  }, [employees]);

  /* ── data loaders ── */
  const loadThreads = async (preserveSelected = true) => {
    const res = await chatAPI.getThreads();
    const rows = res.data?.data || [];
    setThreads(rows);
    if (!preserveSelected && rows.length > 0) setSelectedThreadId(rows[0]._id);
    if (preserveSelected && selectedThreadId && !rows.some((r) => String(r._id) === String(selectedThreadId))) {
      setSelectedThreadId(rows[0]?._id || '');
    }
  };

  const loadEmployees = async () => {
    const res = await chatAPI.getEmployees({
      search: employeeSearch || undefined,
      role: roleFilter || undefined,
      page: 1,
      limit: 200,
    });
    setEmployees(res.data?.data?.items || []);
  };

  const loadThreadMessages = async (threadId) => {
    if (!threadId) return;
    const res = await chatAPI.getThreadMessages(threadId, { page: 1, limit: 200 });
    const rows = res.data?.data?.items || [];
    setMessages(rows);
  };

  /* ── effects ── */
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await Promise.all([loadThreads(false), loadEmployees()]);
      } catch (error) {
        toast.error('تعذر تحميل بيانات المحادثات');
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadEmployees().catch(() => {});
    }, 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSearch, roleFilter]);

  useEffect(() => {
    if (!selectedThreadId) return;
    loadThreadMessages(selectedThreadId).catch(() => {});
    chatAPI.markThreadRead(selectedThreadId).catch(() => {});
    localStorage.setItem('chat_active_thread_id', String(selectedThreadId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId]);

  useEffect(() => {
    localStorage.setItem('chat_viewing', '1');
    return () => {
      localStorage.removeItem('chat_viewing');
      localStorage.removeItem('chat_active_thread_id');
    };
  }, []);

  /* scroll to bottom on new messages */
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  /* ── WebSocket ── */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    const handleThreadUpdated = async () => {
      await loadThreads(true);
      if (selectedThreadId) await loadThreadMessages(selectedThreadId);
    };
    const handleNewMessage = async (payload) => {
      if (String(payload?.thread_id) === String(selectedThreadId)) {
        await loadThreadMessages(selectedThreadId);
        await chatAPI.markThreadRead(selectedThreadId).catch(() => {});
      }
      await loadThreads(true);
    };
    const handleReadUpdated = async (payload) => {
      if (String(payload?.thread_id) === String(selectedThreadId)) {
        await loadThreadMessages(selectedThreadId);
      }
      await loadThreads(true);
    };

    socket.on('chat:thread-updated', handleThreadUpdated);
    socket.on('chat:new-message', handleNewMessage);
    socket.on('chat:read-updated', handleReadUpdated);

    return () => {
      socket.off('chat:thread-updated', handleThreadUpdated);
      socket.off('chat:new-message', handleNewMessage);
      socket.off('chat:read-updated', handleReadUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !selectedThreadId) return undefined;
    socket.emit('chat:join-thread', selectedThreadId);
    return () => {
      socket.emit('chat:leave-thread', selectedThreadId);
    };
  }, [selectedThreadId]);

  /* ── actions ── */
  const toggleRecipient = (id) => {
    setSelectedRecipients((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resetNewThreadForm = () => {
    setNewThreadMessage('');
    setNewThreadImportant(false);
    setSelectedRecipients([]);
    setEmployeeSearch('');
    setRoleFilter('');
  };

  const openNewThread = () => {
    resetNewThreadForm();
    setShowNewThread(true);
    loadEmployees().catch(() => {});
  };

  const closeNewThread = () => {
    setShowNewThread(false);
  };

  const selectThread = (threadId) => {
    setSelectedThreadId(threadId);
    setMobileView('messages');
  };

  const createThread = async () => {
    if (!selectedRecipients.length) return toast.error('اختر موظفًا واحدًا على الأقل');
    if (!newThreadMessage.trim()) return toast.error('اكتب الرسالة');
    try {
      setSending(true);
      const res = await chatAPI.createThread({
        participant_user_ids: selectedRecipients,
        message: newThreadMessage.trim(),
        important: newThreadImportant,
      });
      const createdThreadId = res.data?.data?.thread?._id;
      resetNewThreadForm();
      await loadThreads(true);
      if (createdThreadId) {
        setSelectedThreadId(createdThreadId);
        setMobileView('messages');
      }
      setShowNewThread(false);
      toast.success('تم إنشاء المحادثة وإرسال الرسالة');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل إرسال الرسالة');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!selectedThreadId) return toast.error('اختر محادثة أولاً');
    if (!replyMessage.trim()) return toast.error('اكتب الرسالة');
    try {
      setSending(true);
      await chatAPI.sendMessage(selectedThreadId, {
        message: replyMessage.trim(),
        important: replyImportant,
      });
      setReplyMessage('');
      setReplyImportant(false);
      await Promise.all([loadThreadMessages(selectedThreadId), loadThreads(true)]);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل إرسال الرسالة');
    } finally {
      setSending(false);
    }
  };

  /* ── helpers ── */
  const getParticipants = (thread) =>
    (thread?.participant_user_ids || []).filter((p) => String(p._id) !== String(currentUserId));

  if (loading) {
    return (
      <div className="p-10 text-center text-slate-500" dir="rtl">
        <div className="inline-flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          جاري تحميل نظام المحادثات...
        </div>
      </div>
    );
  }

  /* ====================================================================
   *  RENDER
   * ==================================================================== */
  return (
    <div className="p-3 sm:p-4 lg:p-6" dir="rtl">
      {/* ── Header ── */}
      <div className="mb-3 sm:mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">محادثات المختبر الداخلية</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 hidden sm:block">محادثة الموظفين مع تنبيه المهم وتتبع الرسائل غير المقروءة</p>
        </div>
        <button
          onClick={openNewThread}
          className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-blue-600 text-white text-xs sm:text-sm font-semibold hover:bg-blue-700 shadow-sm transition-all hover:shadow-md shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">محادثة جديدة</span>
          <span className="sm:hidden">جديدة</span>
        </button>
      </div>

      {/* ===== Slide-over: New Thread Panel ===== */}
      {showNewThread && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={closeNewThread} />
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-slideInRight" dir="rtl">
            {/* panel header */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-b border-slate-100">
              <h2 className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                بدء محادثة جديدة
              </h2>
              <button onClick={closeNewThread} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* search + filter */}
            <div className="px-4 sm:px-5 py-3 sm:py-4 space-y-2.5 border-b border-slate-100 bg-slate-50/50">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                <input
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  placeholder="بحث بالاسم أو البريد"
                  className="w-full pr-10 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none"
                />
              </div>
              <div className="relative">
                <Filter className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full pr-10 px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none"
                >
                  <option value="">كل الوظائف</option>
                  {roles.map((r) => (
                    <option key={r} value={r}>{ROLE_DISPLAY[r] || r}</option>
                  ))}
                </select>
              </div>
              {selectedRecipients.length > 0 && (
                <p className="text-xs text-blue-600 font-medium">
                  تم اختيار {selectedRecipients.length} موظف
                </p>
              )}
            </div>

            {/* employees list */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {employees.length === 0 && (
                <p className="p-5 text-sm text-slate-400 text-center">لا يوجد موظفين</p>
              )}
              {employees.map((emp) => {
                const id = String(emp._id);
                const checked = selectedRecipients.includes(id);
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => toggleRecipient(id)}
                    className={`w-full px-4 sm:px-5 py-3 text-right transition-all ${checked ? 'bg-blue-50 border-r-4 border-blue-500' : 'hover:bg-slate-50 border-r-4 border-transparent'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{emp.name}</p>
                        <p className="text-xs text-slate-500 truncate">{emp.email}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[11px] px-2 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-100">
                          {roleLabel(emp.role_id)}
                        </span>
                        {checked && (
                          <span className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                            <CheckCheck className="w-3 h-3 text-white" />
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* compose + send */}
            <div className="px-4 sm:px-5 py-3 sm:py-4 space-y-3 border-t border-slate-100 bg-white">
              <textarea
                value={newThreadMessage}
                onChange={(e) => setNewThreadMessage(e.target.value)}
                rows={3}
                placeholder="اكتب الرسالة الأولى..."
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm resize-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none"
              />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={newThreadImportant}
                  onChange={(e) => setNewThreadImportant(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                رسالة مهمة (إيميل فوري للمستلمين)
              </label>
              <button
                onClick={createThread}
                disabled={sending || !selectedRecipients.length || !newThreadMessage.trim()}
                className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                إنشاء محادثة وإرسال
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Main Chat Area ===== */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex h-[calc(100vh-160px)] sm:h-[76vh]">

          {/* ── Threads sidebar ── */}
          <div className={`${mobileView === 'threads' ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-80 xl:w-96 shrink-0 border-l border-slate-100`}>
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-slate-500" />
                المحادثات
              </h3>
              <span className="text-[11px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{threads.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-400">
                  <MessageCircle className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                  <p>لا توجد محادثات بعد</p>
                  <p className="text-xs mt-1">أنشئ محادثة جديدة للبدء</p>
                </div>
              )}
              {threads.map((thread) => {
                const isActive = String(thread._id) === String(selectedThreadId);
                const participants = getParticipants(thread);
                return (
                  <button
                    type="button"
                    key={thread._id}
                    onClick={() => selectThread(thread._id)}
                    className={`w-full text-right px-4 py-3.5 border-b border-slate-50 transition-all ${isActive ? 'bg-blue-50 border-r-[3px] border-r-blue-500' : 'hover:bg-slate-50 border-r-[3px] border-r-transparent'}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-blue-700">
                          {participants[0]?.name?.charAt(0) || '؟'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">
                              {participants.map((p) => p.name).join('، ') || 'محادثة'}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">
                            {formatTime(thread.last_message_at)}
                          </span>
                        </div>
                        {/* role tag */}
                        {participants.length > 0 && (
                          <div className="flex items-center gap-1 mt-0.5">
                            {participants.slice(0, 2).map((p) => (
                              <span
                                key={p._id}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 truncate max-w-[100px]"
                              >
                                {roleLabel(p.role_id)}
                              </span>
                            ))}
                            {participants.length > 2 && (
                              <span className="text-[10px] text-slate-400">+{participants.length - 2}</span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-slate-500 truncate flex-1 ml-2">
                            {thread.last_message?.message || 'بدون رسائل'}
                          </p>
                          <div className="flex items-center gap-1 shrink-0">
                            {thread.important && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700">مهم</span>
                            )}
                            {(thread.unread_count || 0) > 0 && (
                              <span className="text-[10px] min-w-[18px] text-center px-1.5 py-0.5 rounded-full bg-blue-600 text-white font-bold">
                                {thread.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Messages area ── */}
          <div className={`${mobileView === 'messages' ? 'flex' : 'hidden'} lg:flex flex-col flex-1 min-w-0`}>
            {/* messages header */}
            <div className="px-3 sm:px-5 py-3 border-b border-slate-100 flex items-center gap-3 min-h-[56px]">
              {/* mobile back button */}
              <button
                onClick={() => setMobileView('threads')}
                className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors shrink-0"
              >
                <ArrowRight className="w-5 h-5 text-slate-600" />
              </button>

              <div className="flex-1 min-w-0">
                {selectedThread ? (
                  <>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900 truncate">
                        {getParticipants(selectedThread).map((p) => p.name).join('، ') || 'محادثة'}
                      </h3>
                      {selectedThread?.important && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">
                          <AlertTriangle className="w-3 h-3" />
                          مهمة
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {getParticipants(selectedThread).slice(0, 3).map((p) => (
                        <span
                          key={p._id}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100"
                        >
                          {roleLabel(p.role_id)}
                        </span>
                      ))}
                      <span className="text-[10px] text-slate-400 mr-1">
                        · {formatTime(selectedThread.last_message_at)}
                      </span>
                    </div>
                  </>
                ) : (
                  <h3 className="text-sm text-slate-500">اختر محادثة من القائمة</h3>
                )}
              </div>
            </div>

            {/* messages body */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-gradient-to-b from-slate-50/60 to-white">
              {!selectedThread && (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <MessageCircle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm font-medium">اختر محادثة للبدء</p>
                    <p className="text-xs text-slate-400 mt-1">أو أنشئ محادثة جديدة من الزر أعلاه</p>
                  </div>
                </div>
              )}
              {selectedThread && messages.map((msg) => {
                const mine = String(msg.sender_user_id?._id) === String(currentUserId);
                return (
                  <div key={msg._id} className={`flex ${mine ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-3.5 py-2.5 shadow-sm ${mine ? 'bg-white border border-slate-200' : 'bg-blue-600 text-white'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold ${mine ? 'text-slate-700' : 'text-blue-100'}`}>
                          {msg.sender_user_id?.name}
                        </span>
                        {msg.is_important && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${mine ? 'bg-amber-100 text-amber-700' : 'bg-white/20 text-white'}`}>
                            مهم
                          </span>
                        )}
                      </div>
                      <p className={`text-sm whitespace-pre-wrap leading-relaxed ${mine ? 'text-slate-800' : 'text-white'}`}>{msg.message}</p>
                      <div className={`mt-1.5 text-[11px] ${mine ? 'text-slate-400' : 'text-blue-200'}`}>
                        {formatTime(msg.sent_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* reply box */}
            {selectedThread && (
              <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-t border-slate-100 bg-white space-y-2">
                {/* important toggle */}
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={replyImportant}
                    onChange={(e) => setReplyImportant(e.target.checked)}
                    className="rounded border-slate-300 text-amber-500 focus:ring-amber-400"
                  />
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <span className="text-xs text-slate-600">
                    رسالة مهمة
                    <span className="text-slate-400 mr-1">— عند التفعيل سيتم إرسال إيميل فوري للموظف لتنبيهه</span>
                  </span>
                </label>
                {/* input + send */}
                <div className="flex items-center gap-2">
                  <textarea
                    rows={1}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    placeholder="اكتب ردك... (Enter للإرسال)"
                    className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-slate-200 text-sm resize-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none"
                  />
                  <button
                    onClick={sendReply}
                    disabled={sending || !replyMessage.trim()}
                    className="p-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* animation styles */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slideInRight {
          animation: slideInRight 0.25s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default LabChat;
